import { json, useLoaderData, useParams } from "@remix-run/react";

import { getSdk } from "~/generated/schema.graphcms";
import { getGraphCms } from "~/services/graphcms";
import { getDictionary } from "~/utils/dictionaries";
import { localeTokenMap } from "~/utils/localeTokenMap";

import { mergeMeta } from "../metatags/mergeMeta";

import PlayNowModal from "./$lang._index/components/home/PlayNowModal";
import Pagination from "./$lang._index/components/news/Pagination";
import Tile from "./$lang._index/components/news/Tile";

import type { LoaderFunctionArgs } from "@remix-run/cloudflare";
import type { Brand, GetArticlesQuery, GetNewsPageQuery, Locale } from "~/generated/schema.graphcms";
import type { Dictionary } from "~/utils/dictionaries";

// import type { Metadata } from 'next';
// import { cookies } from 'next/headers';

const slug = "sideswipe-news";

export const meta = mergeMeta<typeof loader>(() => {
	return [
		{ title: "Rocket league Sideswipe News" },
		{ property: "og:title", content: "Rocket League Sideswipe News" },
		{ property: "twitter:title", content: "Rocket League Sideswipe News" },
	];
});

async function getPageData(env: Env, isGamedev: boolean, mappedLocaleToken: string): Promise<GetNewsPageQuery> {
	const { GetNewsPage } = getSdk(getGraphCms(env, isGamedev));
	const pageData = await GetNewsPage({
		locale: [mappedLocaleToken, "en"] as Locale[],
		website_contains_all: ["Sideswipe", "Sideswipe"] as Brand[],
		slug: slug,
	});

	return pageData;
}

async function getArticleData(
	env: Env,
	isGamedev: boolean,
	mappedLocaleToken: string,
	skip: number,
): Promise<GetArticlesQuery["articles"]> {
	const { GetArticles } = getSdk(getGraphCms(env, isGamedev));
	const articleData = await GetArticles({
		locale: [mappedLocaleToken, "en"] as Locale[],
		website_contains_all: ["Sideswipe", "Sideswipe"] as Brand[],
		limit: 12,
		offset: skip,
	});
	const articles = articleData.articles;

	return articles;
}

export default function News(): React.ReactNode {
	const { lang: locale = "en" } = useParams();
	const { pageData, articles, dict } = useLoaderData<typeof loader>();

	const connection = pageData.articlesConnection;

	return (
		<>
			<main>
				<section className="section mx-0 mt-0 px-2 pt-3">
					<div className="container is-fluid mx-0 px-0">
						<div className="columns is-5-mobile is-4-tablet is-3-desktop is-2-widescreen is-2-fullhd is-multiline is-mobile is-vcentered">
							{articles.map((article) => {
								return article.title && article.slug && article?.image?.url ? (
									<Tile key={article.slug} title={article.title} slug={article.slug} imageUrl={article?.image?.url} />
								) : null;
							})}
						</div>
					</div>
				</section>
				<Pagination locale={locale} items={connection.aggregate.count} currentPage={1} pageSize={12} />
			</main>
			<PlayNowModal locale={locale} copy={dict} />
		</>
	);
}

export const loader = async ({ request, params, context }: LoaderFunctionArgs) => {
	const { lang: locale = "en" } = params;
	const url = new URL(request.url);
	const pageParam = url.searchParams.get("page");
	const { env } = context.cloudflare;
	const isGamedev = JSON.parse(env.HYGRAPH_PREVIEW_ENABLED);

	const mappedLocaleToken = localeTokenMap(locale);
	const dict = await getDictionary(locale);

	const page = Number(pageParam || "1");

	if (!Number.isInteger(page) || page <= 0) {
		const redirectUrl = new URL(request.url);
		if (redirectUrl.searchParams.has("page")) {
			redirectUrl.searchParams.delete("page");
		}
		throw new Response(null, {
			status: 302,
			headers: {
				Location: redirectUrl.toString(),
				"Cache-Control": "no-store",
			},
		});
	}

	const newsPage = page;
	const skip = (newsPage - 1) * 12;
	const pageData = await getPageData(env, isGamedev, mappedLocaleToken);
	const articles = await getArticleData(env, isGamedev, mappedLocaleToken, skip);

	return json<{
		dict: Dictionary;
		newsPage: number;
		skip: number;
		pageData: GetNewsPageQuery;
		articles: GetArticlesQuery["articles"];
	}>({ dict, newsPage, skip, pageData, articles });
};
